import { HandPlatter, Droplets, CreditCard, HelpCircle, Check, UserCheck, Clock, AlertTriangle } from "lucide-react";

const TYPE_META = {
  SERVEUR: { icon: HandPlatter, label: "Appel serveur", color: "#F87171", emoji: "🔔" },
  EAU: { icon: Droplets, label: "Demande d'eau", color: "#60A5FA", emoji: "💧" },
  ADDITION: { icon: CreditCard, label: "Souhaite payer", color: "#C69C4A", emoji: "💳" },
  PAIEMENT: { icon: CreditCard, label: "Souhaite payer", color: "#C69C4A", emoji: "💳" },
  AUTRE: { icon: HelpCircle, label: "Demande", color: "#9CA3AF", emoji: "🙋" },
};

const URGENT_SECONDS = 5 * 60; // a request waiting > 5 min is surfaced as priority

function waitSeconds(iso) {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}
function fmtWait(s) {
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h`;
}

export default function WaiterCalls({ requests = [], onClaim, onHandle, compact }) {
  if (requests.length === 0) {
    return <p data-testid="waiter-calls-empty" className="text-sm text-stone-500 bg-[#111F1A] border border-[#1B3028] rounded-2xl p-4">Aucun appel serveur en attente.</p>;
  }
  // Mini-stats (real, from the active list): count + average current wait.
  const waits = requests.map((r) => waitSeconds(r.created_at));
  const avg = waits.length ? Math.round(waits.reduce((a, b) => a + b, 0) / waits.length) : 0;
  const urgentCount = waits.filter((w) => w >= URGENT_SECONDS).length;
  // Priority first: longest waiting on top.
  const ordered = [...requests].sort((a, b) => waitSeconds(b.created_at) - waitSeconds(a.created_at));

  return (
    <div className="space-y-3">
      <div data-testid="waiter-calls-stats" className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="rounded-full bg-[#111F1A] border border-[#1B3028] px-3 py-1 text-stone-300">{requests.length} en attente</span>
        <span className="rounded-full bg-[#111F1A] border border-[#1B3028] px-3 py-1 text-stone-400">Attente moy. {fmtWait(avg)}</span>
        {urgentCount > 0 && <span className="rounded-full bg-red-500/10 border border-red-500/40 px-3 py-1 text-red-300 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {urgentCount} prioritaire{urgentCount > 1 ? "s" : ""}</span>}
      </div>
      <div data-testid="waiter-calls" className={`grid gap-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {ordered.map((r) => {
          const meta = TYPE_META[(r.type || "SERVEUR").toUpperCase()] || TYPE_META.AUTRE;
          const w = waitSeconds(r.created_at);
          const urgent = w >= URGENT_SECONDS;
          const status = r.claimed ? "Prise en charge" : "Nouvelle";
          return (
            <div key={r.id} data-testid={`waiter-call-${r.table_number}`}
              className={`rounded-2xl bg-[#111F1A] border p-4 ${urgent ? "ring-2 ring-red-500/50" : ""}`}
              style={{ borderColor: urgent ? "#F8717188" : `${meta.color}55` }}>
              <div className="flex items-center justify-between">
                <span className="font-serif-display text-xl text-white">{meta.emoji} Table {r.table_number}</span>
                <span className={`text-[11px] flex items-center gap-1 ${urgent ? "text-red-300 font-semibold" : "text-stone-500"}`}><Clock className="w-3 h-3" /> {fmtWait(w)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm font-semibold" style={{ color: meta.color }}>{r.label || meta.label}</p>
                <span data-testid={`waiter-status-${r.id}`} className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${r.claimed ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-[#C69C4A]/10 text-[#C69C4A] border border-[#C69C4A]/30"}`}>{status}</span>
              </div>
              {r.claimed && r.claimed_by && (
                <p className="text-[11px] text-emerald-400 mt-1 flex items-center gap-1"><UserCheck className="w-3 h-3" /> Pris par {r.claimed_by}</p>
              )}
              <div className="flex gap-2 mt-3">
                {!r.claimed && (
                  <button data-testid={`waiter-claim-${r.id}`} onClick={() => onClaim(r.id)}
                    className="flex-1 rounded-full bg-[#1A3D32] hover:bg-[#123026] border border-[#2C5446] text-stone-100 text-xs font-bold py-2.5">
                    Prendre en charge
                  </button>
                )}
                <button data-testid={`waiter-done-${r.id}`} onClick={() => onHandle(r.id)}
                  className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold py-2.5 flex items-center justify-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Terminé
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
